//상단 글자색(언어 선택, 탭, X버튼) 계산
//관리자 커버 설정의 TOP_COLOR_AUTO 가 켜져 있으면 배경 색상(BG_COLOR) 밝기에 맞춰 흰 글자/검은 글자를 고르고,
//꺼져 있으면 관리자가 지정한 TOP_TEXT1_COLOR(선택), TOP_TEXT2_COLOR(미선택), TOP_CLOSE_COLOR(X버튼)를 쓴다.
//참가자 사이트(BlueOrigin_Webflow)의 src/js/topcolor.js 와 같은 내용이다. 한쪽을 고치면 다른 쪽도 같이 고친다.

//관리자 커버 설정의 상단 글자색 기본값
const MANUAL_DEFAULT = {
  TOP_TEXT1_COLOR: "#FFFFFFFF",
  TOP_TEXT2_COLOR: "#AAAAAAFF",
  TOP_CLOSE_COLOR: "#7F7F7FFF",
};

//어두운 배경: 지금까지 화면에 쓰던 색과 같다 (선택 흰색, 언어 미선택 gray_30, 탭 미선택·X버튼 gray_40)
const DARK_BG = { SELECT: "#FFFFFF", UNSELECT: "#AAAAAA", TAB_UNSELECT: "#7F7F7F", CLOSE: "#7F7F7F" };
//밝은 배경
const LIGHT_BG = { SELECT: "#111111", UNSELECT: "#666666", TAB_UNSELECT: "#666666", CLOSE: "#666666" };

//배경 색상이 반투명이면 비쳐 보이는 페이지 바탕 (index.html body 검정)
const PAGE_BG = [0, 0, 0];

//"#RGB", "#RGBA", "#RRGGBB", "#RRGGBBAA"(# 없어도 됨), "rgb()/rgba()", "transparent" → [r, g, b, a(0~1)], 못 읽으면 null
export function parseColor(value) {
  if (typeof value != "string") return null;
  const text = value.trim().toLowerCase();
  if (text == "transparent") return [0, 0, 0, 0];

  const rgb = text.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/);
  if (rgb) {
    return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3]), rgb[4] === undefined ? 1 : Number(rgb[4])];
  }

  let hex = text.replace(/^#/, "");
  if (!/^[0-9a-f]+$/.test(hex)) return null;
  if (hex.length == 3 || hex.length == 4) hex = hex.split("").map((ch) => ch + ch).join("");
  if (hex.length == 6) hex += "ff";
  if (hex.length != 8) return null;

  const num = [0, 2, 4, 6].map((i) => parseInt(hex.substr(i, 2), 16));
  return [num[0], num[1], num[2], num[3] / 255];
}

//WCAG 상대 휘도
function luminance(rgb) {
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

//검은 글자가 흰 글자보다 대비가 큰 배경이면 true. 못 읽는 값은 어두운 배경(기존 화면)으로 본다.
export function isLightColor(value) {
  const color = parseColor(value);
  if (!color) return false;
  const alpha = color[3];
  const rgb = [0, 1, 2].map((i) => color[i] * alpha + PAGE_BG[i] * (1 - alpha));
  return luminance(rgb) > 0.179;
}

//자동 여부. TOP_COLOR_AUTO 를 저장한 적이 없는 기존 행사는 자동으로 본다.
//기존 상단 글자색(TOP_TEXT1/TEXT2/CLOSE)은 행사 정보 화면에만 쓰이던 값이라 QR 입장권 화면 등에 그대로 쓰면 안 된다.
//(예: 2026 배민광고 컨퍼런스는 흰 배경에 세 색을 모두 흰색으로 저장해 두었다)
export function isAutoTopColor(cover) {
  if (!cover) return true;
  if (cover.TOP_COLOR_AUTO === undefined || cover.TOP_COLOR_AUTO === null) return true;
  return !!cover.TOP_COLOR_AUTO;
}

//상단 글자색을 기본값 그대로 두었는지 (값이 없으면 기본값으로 본다)
export function isDefaultTopColor(cover) {
  if (!cover) return true;
  return Object.keys(MANUAL_DEFAULT).every((key) => {
    if (!cover[key]) return true;
    const saved = parseColor(cover[key]);
    const base = parseColor(MANUAL_DEFAULT[key]);
    return saved != null && saved.every((v, i) => Math.abs(v - base[i]) < 0.01);
  });
}

export function autoTopColors(bgColor) {
  return isLightColor(bgColor) ? LIGHT_BG : DARK_BG;
}

//화면에 쓸 상단 색 { SELECT, UNSELECT, TAB_UNSELECT, CLOSE }
export function getTopColors(cover) {
  if (!cover) return DARK_BG;
  const auto = autoTopColors(cover.BG_COLOR);
  if (isAutoTopColor(cover)) return auto;

  //직접 지정: 못 읽는 값만 자동 색으로 채운다
  const pick = (value, fallback) => {
    const color = parseColor(value);
    return color ? `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})` : fallback;
  };
  return {
    SELECT: pick(cover.TOP_TEXT1_COLOR, auto.SELECT),
    UNSELECT: pick(cover.TOP_TEXT2_COLOR, auto.UNSELECT),
    TAB_UNSELECT: pick(cover.TOP_TEXT2_COLOR, auto.TAB_UNSELECT),
    CLOSE: pick(cover.TOP_CLOSE_COLOR, auto.CLOSE),
  };
}

//# 이 빠진 색 값("FFFFFFFF")에 # 을 붙인다. 이름이 COLOR 로 끝나는 값만 고치고, 안쪽 객체(글꼴의 COLOR 등)도 같이 본다.
//관리자 커버 설정의 등록확인·등록신청 글자색 기본값이 # 없이 저장돼 와서, 화면에서 색이 무시되고 회색으로 보였다.
export function fixColorHash(cover) {
  if (!cover || typeof cover != "object") return cover;
  Object.keys(cover).forEach((key) => {
    const value = cover[key];
    if (value && typeof value == "object") {
      fixColorHash(value);
    } else if (typeof value == "string" && /COLOR$/i.test(key) && /^([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value.trim())) {
      cover[key] = "#" + value.trim();
    }
  });
  return cover;
}
